<template>
    <div class="">
        <div>
            <!-- Main Card -->
            <div class="ppv2-py-4">
                <!-- DataTable Component -->
                <DataTable
                    :perPage="itemsPerPage"
                    :key="'dt-' + tableKey"
                    :columns="tableColumns"
                    :items="paginatedExperiences"
                    :currentPage="currentPage"
                    :totalPages="totalPages"
                    :selectedItems="selectedItems"
                    :enableDragDrop="enableDragDrop"
                    :enableMultiSelect="false"
                    @page-changed="handlePageChange"
                    @item-selected="toggleItemSelection"
                    @select-all-toggled="toggleSelectAll"
                    @drag-end="handleDragEnd"
                    :use_index="true"
                    @sort="onSort"
                    :rowClass="getRowClass"
                >
                    <!-- Toolbar Slot -->
                    <template #toolbar>
                        <div class="ppv2-flex ppv2-flex-wrap ppv2-items-center" :style="{ gap: normalizeSize(16, true) }">
                            <!-- Filter Button -->

                            <!-- Search Input -->
                            <div class="ppv2-relative">
                                <Search v-model="searchQuery" :emitGlobal="false" @search-updated="handleSearchUpdate" />
                            </div>

                            <!-- Export Button -->
                            <ExportSelect
                                :label="$t('experiences.actions.export')"
                                :options="exportOptions"
                                @export-selected="exportData"
                                :use_only_csv="use_only_csv"
                            />

                            <FilterByType
                                :label="$t('experiences.filters.filterByType')"
                                :options="filterOptions"
                                @filter-selected="selectType"
                            />

                            <div
                                v-if="selectedType === 'experience' || selectedType === 'pack'"
                                class="ppv2-flex ppv2-items-center ppv2-gap-2"
                            >
                                <FilterByType
                                    :label="$t('experiences.filters.filterByTag')"
                                    :options="availableTagOptions"
                                    icon="Hash"
                                    :selected="selectedTagId"
                                    @filter-selected="handleTagFilterChange"
                                />
                            </div>

                            <!-- Create Button -->
                            <BaseButton
                                v-if="canConsultExperience || canConsultForfait || canConsultFormule || canConsultAbonnement"
                                variant="purple"
                                :custom-height="40"
                                :custom-font-size="14"
                                custom-class="ppv2-ml-auto ppv2-gap-3 ppv2-pl-4 ppv2-pr-[20px]"
                                @click="openCreateTab"
                            >
                                <Plus :size="17" />
                                <span class="poppinsRegular">{{ $t('experiences.actions.create') }}</span>
                            </BaseButton>
                        </div>
                    </template>

                    <!-- Column: Name -->
                    <template #column-name="{ item }">
                        <div class="ppv2-flex ppv2-flex-col ppv2-justify-center">
                            <div class="ppv2-flex ppv2-items-center">
                                <a
                                    :href="getEditUrl(item)"
                                    @click.prevent="openEditTab(item)"
                                    class="ppv2-text-black hover:ppv2-text-[#7D2DBD] hover:ppv2-underline ppv2-transition-colors poppinsRegular"
                                    :style="{ fontSize: normalizeFontSize(14) }"
                                    :title="$t('experiences.actions.edit')"
                                >
                                    {{ item.name }}
                                </a>
                                <Tooltip
                                    v-if="item.draft == 0"
                                    :text="$t('experiences.actions.preview')"
                                    position="right"
                                    class="ppv2-ml-2"
                                >
                                    <a
                                        class="ppv2-text-black hover:ppv2-text-[#7D2DBD] hover:ppv2-underline hover:ppv2-cursor-pointer ppv2-flex"
                                        :href="getItemLink(item)"
                                        :target="
                                            ['experience', 'forfait', 'pack', 'abonnement'].includes(item.type) ? '_blank' : null
                                        "
                                    >
                                        <Eye :size="14" />
                                    </a>
                                </Tooltip>
                            </div>
                            <span
                                v-if="item.updated_at"
                                class="ppv2-text-gray-400 ppv2-text-[11px] poppinsRegular ppv2-mt-0.5"
                                :title="$t('experiences.table.lastModified') + ' ' + formatDate(item.updated_at)"
                            >
                                {{ $t('experiences.table.lastModified') }} {{ formatDate(item.updated_at) }}
                            </span>
                        </div>
                    </template>

                    <!-- active -->
                    <template #column-state="{ item }">
                        <button
                            :disabled="item.state.toLowerCase() === 'masquée' && item.status.toLowerCase() !== 'publiée'"
                            @click="item.status.toLowerCase() === 'publiée' && toggleState(item)"
                            class="ppv2-min-w-[135px] ppv2-inline-flex ppv2-items-center ppv2-gap-2 ppv2-px-3 ppv2-py-2 ppv2-rounded-xl ppv2-border ppv2-border-solid ppv2-border-1"
                            :class="
                                item.state.toLowerCase() === 'affichée'
                                    ? 'ppv2-border-[#206A64] ppv2-bg-[#06B217] ppv2-bg-opacity-20'
                                    : 'ppv2-border-[#4E4E4E] ppv2-bg-[#474747] ppv2-bg-opacity-20'
                            "
                        >
                            <div
                                class="ppv2-w-9 ppv2-h-5 ppv2-rounded-full ppv2-flex ppv2-items-center"
                                :class="
                                    item.state.toLowerCase() === 'affichée'
                                        ? 'ppv2-bg-[#206A64] ppv2-justify-end'
                                        : 'ppv2-bg-[#4E4E4E] ppv2-justify-start'
                                "
                            >
                                <div class="ppv2-w-3.5 ppv2-h-3.5 ppv2-bg-white ppv2-rounded-full ppv2-mx-0.5"></div>
                            </div>

                            <span class="ppv2-text-black poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">
                                {{ item.state.charAt(0).toUpperCase() + item.state.slice(1) }}
                            </span>
                        </button>
                    </template>

                    <!-- Column: Type -->
                    <template #column-type="{ item }">
                        <div
                            class="textGray ppv2-inline-flex ppv2-px-3 ppv2-rounded-xl ppv2-text-base md:ppv2-text-lg"
                            :class="{
                                'bgExperience ppv2-bg-opacity-30': item.type === 'experience',
                                'bgForfait ppv2-bg-opacity-30': item.type === 'forfait',
                                'bgAbonnement ppv2-bg-opacity-30': item.type === 'abonnement',
                                'bgPack ppv2-bg-opacity-30': item.type === 'pack',
                            }"
                            :style="{ fontSize: normalizeFontSize(14) }"
                        >
                            {{ getTypeLabel(item.type) }}
                        </div>
                    </template>

                    <template #column-tag_name="{ item }">
                        <div
                            v-if="item.type === 'experience' || item.type === 'pack'"
                            class="ppv2-relative"
                            :ref="'tagDropdown-' + getUniqueId(item)"
                        >
                            <button
                                @click.stop="toggleTagDropdown(getUniqueId(item))"
                                class="ppv2-inline-flex ppv2-items-center ppv2-gap-2 ppv2-px-3 ppv2-py-2 ppv2-rounded-xl ppv2-border ppv2-border-solid ppv2-transition-all"
                                :class="
                                    item.tag_id
                                        ? 'ppv2-border-[#7D2DBD] ppv2-bg-[#7D2DBD] ppv2-bg-opacity-10 ppv2-text-[#7D2DBD]'
                                        : 'ppv2-border-gray-300 ppv2-bg-white textGray'
                                "
                                :style="{ fontSize: normalizeFontSize(14) }"
                                :disabled="updatingTag[getUniqueId(item)]"
                            >
                                <span class="poppinsRegular ppv2-truncate ppv2-max-w-[120px]">
                                    {{ item.tag_name || $t('experiences.table.noTag') }}
                                </span>
                                <ChevronDown
                                    class="ppv2-w-4 ppv2-h-4 ppv2-flex-shrink-0"
                                    :class="{ 'ppv2-rotate-180': activeTagDropdown === getUniqueId(item) }"
                                />
                            </button>

                            <!-- Fixed Tag Dropdown -->
                            <div
                                v-if="activeTagDropdown === getUniqueId(item)"
                                class="menuDropdown ppv2-fixed ppv2-z-[100] ppv2-min-w-[150px] ppv2-max-h-[200px] ppv2-max-w-[200px] ppv2-overflow-y-auto ppv2-bg-white ppv2-rounded-xl ppv2-shadow-lg ppv2-border ppv2-border-gray-200"
                                :style="tagDropdownPosition"
                                :ref="'tagMenu-' + getUniqueId(item)"
                            >
                                <button
                                    class="ppv2-w-full ppv2-text-left ppv2-px-4 ppv2-py-2 poppinsRegular text-sm hover:ppv2-bg-gray-100 ppv2-transition-colors"
                                    @click="updateItemTag(item, null)"
                                >
                                    <span
                                        :class="{
                                            'ppv2-text-[#7D2DBD]': !item.tag_id || item.tag_id.length === 0 || !item.tag_id[0],
                                        }"
                                    >
                                        {{ $t('experiences.table.noTag') }}
                                    </span>
                                </button>
                                <button
                                    v-for="tag in availableTags"
                                    :key="tag.id"
                                    class="ppv2-w-full ppv2-text-left ppv2-px-4 ppv2-py-2 poppinsRegular text-sm hover:ppv2-bg-gray-100 ppv2-transition-colors"
                                    @click="updateItemTag(item, tag.id)"
                                >
                                    <span :class="{ 'ppv2-text-[#7D2DBD]': isTagSelected(item, tag.id) }">
                                        {{ tag.name }}
                                    </span>
                                </button>
                            </div>
                        </div>
                        <span v-else class="ppv2-text-black poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">
                            -
                        </span>
                    </template>

                    <!-- Column: Subcategory -->
                    <!-- <template #column-subcategory_name="{ item }">
                        <span class="ppv2-text-black poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">
                            {{ item.subcategory_name || '-' }}
                        </span>
                    </template> -->

                    <!-- Column: Status -->
                    <template #column-status="{ item }">
                        <button
                            class="ppv2-inline-flex ppv2-min-h-[28px] ppv2-items-center ppv2-justify-center ppv2-gap-2 ppv2-px-3 ppv2-rounded-xl ppv2-transition-all"
                            :class="
                                item.status.toLowerCase() === 'publiée'
                                    ? 'ppv2-border-[#206A64] ppv2-bg-[#06B217] ppv2-bg-opacity-20'
                                    : 'ppv2-border-[#4E4E4E] ppv2-bg-[#474747] ppv2-bg-opacity-20'
                            "
                        >
                            <span class="ppv2-text-black poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">
                                {{
                                    item.status.toLowerCase() === 'publiée'
                                        ? $t('experiences.status.published')
                                        : $t('experiences.status.draft')
                                }}
                            </span>
                        </button>
                    </template>

                    <!-- Column: Actions -->
                    <template #column-actions="{ item }">
                        <div
                            class="ppv2-flex ppv2-items-center ppv2-justify-center ppv2-gap-3"
                            :style="{ fontSize: normalizeFontSize(14) }"
                        >
                            <button
                                class="ppv2-p-1 ppv2-text-black hover:ppv2-bg-black hover:ppv2-bg-opacity-10 ppv2-rounded ppv2-transition-all"
                                :title="$t('experiences.actions.edit')"
                                @click="openEditTab(item)"
                            >
                                <SquarePen :size="18" />
                            </button>
                            <button
                                @click="duplicated(item)"
                                class="ppv2-p-1 ppv2-text-black hover:ppv2-bg-black hover:ppv2-bg-opacity-10 ppv2-rounded ppv2-transition-all"
                                :title="$t('experiences.actions.duplicate')"
                            >
                                <CopyPlus :size="18" />
                            </button>
                            <button
                                class="btnTrash ppv2-p-1 hover:ppv2-bg-[#FF9C9C] hover:ppv2-bg-opacity-10 ppv2-rounded ppv2-transition-all"
                                :title="$t('experiences.actions.delete')"
                                @click="deleteSingleItem(item)"
                            >
                                <Trash2 :size="18" />
                            </button>
                        </div>
                    </template>

                    <!-- Bulk Actions Slot -->
                    <template #bulk-actions="{ selectedCount }">
                        <span class="poppinsRegular textGray" :style="{ fontSize: normalizeFontSize(15) }">
                            {{ $tc('experiences.selection.itemsSelected', selectedCount, { count: selectedCount }) }}
                        </span>
                        <span
                            class="poppinsRegular ppv2-text-white ppv2-text-opacity-50"
                            :style="{ fontSize: normalizeFontSize(15) }"
                            >|</span
                        >
                        <BaseButton
                            variant="outline"
                            @click="deleteSelectedItems"
                            customClass="!ppv2-px-4 !ppv2-h-[30px] !ppv2-flex !ppv2-items-center !ppv2-gap-2"
                        >
                            <Trash2 :size="14" />
                            <span :style="{ fontSize: normalizeFontSize(14) }">{{ $t('common.deleteSelection') }}</span>
                        </BaseButton>
                    </template>
                </DataTable>
            </div>
        </div>

        <!-- Delete Confirmation Popup -->
        <CustomDialog v-model="showDeletePopup" :defaultTitle="deletePopupTitle" customClassTitle="poppinsSemiBold" noScroll>
            <template #content>
                <p class="ppv2-text-center ppv2-text-black poppinsRegular" :style="{ fontSize: normalizeFontSize(15) }">
                    {{ deletePopupDescription }}
                </p>
            </template>
            <template #actions>
                <BaseButton
                    id="button"
                    type="button"
                    variant="outline"
                    @click="showDeletePopup = false"
                    customClass="!ppv2-min-h-[40px] !ppv2-text-base !ppv2-min-w-[170px]"
                    :customHeight="45"
                >
                    {{ $t('experiences.deletePopup.cancel') }}
                </BaseButton>
                <BaseButton
                    type="submit"
                    variant="danger"
                    @click="confirmDelete"
                    customClass="!ppv2-min-h-[40px] !ppv2-text-base !ppv2-min-w-[170px]"
                    :customHeight="45"
                >
                    {{ $t('experiences.deletePopup.confirm') }}
                </BaseButton>
            </template>
        </CustomDialog>
    </div>
</template>

<script>
import { Plus, CopyPlus, Trash2, SquarePen, ChevronDown, Hash, Check, Eye } from 'lucide-vue'
import normalizeSize from '../../../utils/normalizeSize'
import Search from './components/Search.vue'
import BaseButton from '../../Shared/BaseButton.vue'
import CustomDialog from '../../Shared/CustomDialog.vue'
import FilterByType from '../../Shared/FilterByType.vue'
import ExportSelect from '../../Shared/ExportSelect.vue'
import DataTable from '../../Shared/DataTable.vue'
import Tooltip from '../../Shared/Tooltip.vue'
import { eventBus } from '../../Customer/eventBus'
import normalizeFontSize from '../../../utils/normalizeFontSize'
import { getPerPage, setPerPage } from '../../../utils/perPage'

export default {
    components: {
        Plus,
        SquarePen,
        CopyPlus,
        Trash2,
        Check,
        Search,
        ChevronDown,
        Eye,
        BaseButton,
        CustomDialog,
        FilterByType,
        ExportSelect,
        DataTable,
        Tooltip,
    },
    props: {
        initialType: {
            type: String,
            default: 'tout',
        },
        urlFrontReservation: {
            type: String,
            default: '',
        },
        token: {
            type: String,
            default: '',
        },
        canConsultExperience: {
            type: [Boolean, String],
            default: true,
        },
        canConsultForfait: {
            type: [Boolean, String],
            default: true,
        },
        canConsultFormule: {
            type: [Boolean, String],
            default: true,
        },
        canConsultAbonnement: {
            type: [Boolean, String],
            default: true,
        },
    },
    data() {
        return {
            searchQuery: '',
            selectedType: this.initialType,
            currentPage: 1,
            itemsPerPage: getPerPage('experiences_items_per_page', 10),
            nbreOfPages: 1,
            apiStrategy: 'manual', // Controlled strategy parameter
            selectedItems: [], // Tableau pour stocker les IDs des éléments sélectionnés
            selectAll: false, // État pour la case à cocher "Tout sélectionner"
            isLoading: false,
            error: null,
            isSavingPositions: false,
            tableColumns: [
                { key: 'name', label: this.$t('experiences.table.name'), sortable: true, type: 'text' },
                { key: 'tag_name', label: this.$t('experiences.table.tag'), sortable: false, type: 'select' },
                // { key: 'subcategory_name', label: this.$t('experiences.table.subcategory'), sortable: true, type: 'text' },
                { key: 'status', label: this.$t('experiences.table.status'), sortable: true, type: 'badge' },
                { key: 'type', label: this.$t('experiences.table.type'), sortable: true, type: 'badge' },
                { key: 'state', label: this.$t('experiences.table.state'), sortable: true, type: 'badge' },
                { key: 'actions', label: this.$t('experiences.table.actions'), sortable: false, type: 'actions' },
            ],
            showDeletePopup: false,
            deletePopupTitle: this.$t('experiences.deletePopup.title'),
            deletePopupDescription: '',
            deleteAction: null, // 'single' or 'multiple'
            itemToDelete: null, // ID of item to delete for single deletion
            filterOptions: [
                { value: 'tout', label: this.$t('experiences.filters.all') },
                { value: 'experience', label: this.$t('experiences.filters.experience') },
                { value: 'pack', label: this.$t('experiences.filters.pack') },
                { value: 'forfait', label: this.$t('experiences.filters.forfait') },
                { value: 'abonnement', label: this.$t('experiences.filters.abonnement') },
            ],
            exportOptions: [
                { value: 'csv', label: this.$t('experiences.export.csv') },
                { value: 'xls', label: this.$t('experiences.export.pdf') },
            ],
            experiences: {
                data: [],
                total: 0,
            },
            sortKey: 'id',
            sortOrder: 'desc',
            use_only_csv: true,
            tableKey: 0,
            // enableDragDrop is now computed
            availableTags: [],
            updatingTag: {},
            selectedTagId: '',
            activeTagDropdown: null,
            tagDropdownPosition: { top: '0px', left: '0px' },
            duplicatedItemKey: null,
        }
    },

    computed: {
        enableDragDrop() {
            if (this.isSavingPositions) return false
            // Disable drag & drop during search: user can only see a subset of items,
            // so reordering while filtered would produce incorrect global positions.
            if (this.searchQuery && this.searchQuery.trim() !== '') return false
            // Same reason for a column sort: as soon as sort_by !== 'id' the backend drops the
            // manual strategy (ExperienceService::applySorting), so the rows come back in
            // alphabetical/status order and positions derived from that display would be meaningless.
            if (this.sortKey !== 'id') return false
            return this.selectedType !== 'tout' || this.apiStrategy === 'manual'
        },
        availableTagOptions() {
            const options = [{ value: '', label: this.$t('experiences.filters.all') }]
            this.availableTags.forEach((tag) => {
                options.push({ value: tag.id, label: tag.name })
            })
            return options
        },
        filteredExperiences() {
            const search = this.searchQuery?.toLowerCase().trim() || ''
            const type = this.selectedType
            if (!this.experiences?.data) return []

            const filtered = this.experiences.data.filter((e) => {
                const name = e.name?.toLowerCase() || ''
                const matchesSearch = !search || name.includes(search)
                const matchesType = !type || type === 'all' || type === 'tout' || e.type === type
                return matchesSearch && matchesType
            })
            //if have name or state or etat use trie
            // Do not sort locally if strategy is 'manual': the backend owns the order
            const isBackendOrdered = this.apiStrategy === 'manual'
            if (this.sortKey !== 'id' || isBackendOrdered) {
                return filtered
            }
            return [...filtered].sort((a, b) => {
                // Just duplicated items always go first
                if (a._justDuplicated && !b._justDuplicated) return -1
                if (b._justDuplicated && !a._justDuplicated) return 1

                const aHasOrder = a.order > -1
                const bHasOrder = b.order > -1

                // Both have order → sort by order ASC
                if (aHasOrder && bHasOrder) {
                    return a.order - b.order
                }

                // Only A has order → A first
                if (aHasOrder) return -1

                // Only B has order → B first
                if (bHasOrder) return 1

                // Neither has order → sort by id DESC
                return b.id - a.id
            })
        },
        paginatedExperiences() {
            return this.filteredExperiences || []
        },
        totalPages() {
            return this.experiences.total
        },
    },
    mounted() {
        eventBus.$on('dt-selected-perpage-changed', this.handlePerPageChange)
        this.fetchExperiences()
        this.fetchAvailableTags()
        document.addEventListener('click', this.handleClickOutside)
        window.addEventListener('scroll', this.updateTagDropdownPosition, true)
        window.addEventListener('resize', this.updateTagDropdownPosition)
    },
    beforeDestroy() {
        eventBus.$off('dt-selected-perpage-changed', this.handlePerPageChange)
        document.removeEventListener('click', this.handleClickOutside)
        window.removeEventListener('scroll', this.updateTagDropdownPosition, true)
        window.removeEventListener('resize', this.updateTagDropdownPosition)
    },
    methods: {
        normalizeFontSize,
        normalizeSize,
        formatDate(dateString) {
            if (!dateString) return ''
            const date = new Date(dateString)
            return date
                .toLocaleDateString('fr-FR', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                })
                .replace(',', ' à')
        },
        getUniqueId(item) {
            return `${item.type}-${item.id}`
        },
        async toggleState(item) {
            const previousStatus = item.state
            const newStatus = item.state.toLowerCase() === 'affichée' ? 'masquée' : 'affichée'
            const newActive = newStatus === 'affichée' ? 1 : 0
            item.state = newStatus

            try {
                const response = await axios.patch(
                    '/api/v3/experience/update-visibility',
                    {
                        id: item.id,
                        active: newActive,
                        type: item.type,
                    },
                    {
                        headers: {
                            Authorization: `Bearer ${this.token}`,
                        },
                    }
                )

                if (!response.data.success) {
                    // Revert on API failure
                    item.state = previousStatus
                    const message = response.data.message || 'Erreur lors de la mise à jour du statut'
                    this.$toast.error(message)
                }
            } catch (error) {
                this.$toast.error(error.response.data.message)
                item.state = previousStatus
            }
        },
        getTypeLabel(type) {
            const labels = {
                experience: this.$t('experiences.filters.experience'),
                forfait: this.$t('experiences.filters.forfait'),
                abonnement: this.$t('experiences.filters.abonnement'),
                pack: this.$t('experiences.filters.pack'),
            }
            return labels[type] || type
        },
        handlePageChange(page) {
            this.currentPage = page
            this.fetchExperiences()
        },
        selectType(type) {
            this.selectedType = type
            this.isFilterDropdownOpen = false
            this.currentPage = 1
            this.itemsPerPage = 10
            this.fetchExperiences()
        },
        handleSearchUpdate(searchValue) {
            this.searchQuery = searchValue
            this.currentPage = 1
            this.fetchExperiences()
        },
        handleTagFilterChange(tagId) {
            this.selectedTagId = tagId
            this.currentPage = 1
            this.fetchExperiences()
        },
        toggleTagDropdown(itemId) {
            if (this.activeTagDropdown === itemId) {
                this.activeTagDropdown = null
            } else {
                this.activeTagDropdown = itemId
                this.$nextTick(() => {
                    this.calculateTagDropdownPosition(itemId)
                })
            }
        },
        calculateTagDropdownPosition(itemId) {
            const buttonRefName = 'tagDropdown-' + itemId
            const button = this.$refs[buttonRefName]
            // refs in v-for might be arrays
            const btnEl = Array.isArray(button) ? button[0] : button
            if (!btnEl) return

            const rect = btnEl.getBoundingClientRect()
            const DROPDOWN_WIDTH = 192 // w-48 = 12rem = 192px
            const MARGIN = 8 // safe edge margin in px
            const viewportWidth = window.innerWidth

            let left = rect.left
            if (left + DROPDOWN_WIDTH > viewportWidth - MARGIN) {
                left = viewportWidth - DROPDOWN_WIDTH - MARGIN
            }
            if (left < MARGIN) {
                left = MARGIN
            }

            this.tagDropdownPosition = {
                top: `${rect.bottom + 4}px`,
                left: `${left}px`,
                width: `${Math.min(DROPDOWN_WIDTH, viewportWidth - MARGIN * 2)}px`,
            }
        },

        updateTagDropdownPosition() {
            if (this.activeTagDropdown) {
                this.calculateTagDropdownPosition(this.activeTagDropdown)
            }
        },
        handleClickOutside(event) {
            if (this.activeTagDropdown) {
                const buttonRefName = 'tagDropdown-' + this.activeTagDropdown
                const menuRefName = 'tagMenu-' + this.activeTagDropdown

                const button = this.$refs[buttonRefName]
                const menu = this.$refs[menuRefName]

                const btnEl = Array.isArray(button) ? button[0] : button
                const menuEl = Array.isArray(menu) ? menu[0] : menu

                const clickedButton = btnEl && (btnEl === event.target || btnEl.contains(event.target))
                const clickedMenu = menuEl && (menuEl === event.target || menuEl.contains(event.target))

                if (!clickedButton && !clickedMenu) {
                    this.activeTagDropdown = null
                }
            }
        },

        // Méthodes pour la sélection multiple
        toggleItemSelection(itemId) {
            const index = this.selectedItems.indexOf(itemId)
            if (index > -1) {
                // Retirer l'élément de la sélection
                this.selectedItems.splice(index, 1)
            } else {
                // Ajouter l'élément à la sélection
                this.selectedItems.push(itemId)
            }
            this.selectAll = this.selectedItems.length === this.paginatedExperiences.length
        },

        toggleSelectAll() {
            const allIndexes = this.paginatedExperiences.map((_, index) => index)
            const allSelected = allIndexes.length > 0 && allIndexes.every((idx) => this.selectedItems.includes(idx))
            if (allSelected) {
                this.selectedItems = []
            } else {
                this.selectedItems = [...allIndexes]
            }
        },

        async deleteSelectedItems() {
            if (this.selectedItems.length === 0) return

            this.deleteAction = 'multiple'
            this.deletePopupDescription = this.$t('experiences.deletePopup.message')
            this.showDeletePopup = true
        },

        deleteSingleItem(item) {
            this.deleteAction = 'single'
            this.itemToDelete = item
            this.deletePopupDescription = this.$t('experiences.deletePopup.message')
            this.showDeletePopup = true
        },

        async confirmDelete() {
            if (this.deleteAction == 'multiple') {
                try {
                    const objs = this.selectedItems.map((idx) => this.paginatedExperiences[idx])
                    await axios.delete('/api/v3/experience/delete', {
                        data: {
                            objects: objs,
                        },
                        headers: {
                            Authorization: `Bearer ${this.token}`,
                        },
                    })

                    // Rafraîchir la liste après suppression
                    await this.fetchExperiences()

                    // Réinitialiser la sélection
                    this.selectedItems = []
                    this.selectAll = false
                } catch (error) {
                    this.$toast.error(error.response.data.message)
                    console.error('Delete error:', error)
                    alert(this.$t('common.error'))
                }
            } else if (this.deleteAction == 'single') {
                try {
                    await axios.delete('/api/v3/experience/delete', {
                        data: {
                            objects: [this.itemToDelete],
                        },
                        headers: {
                            Authorization: `Bearer ${this.token}`,
                        },
                    })

                    // Rafraîchir la liste après suppression
                    await this.fetchExperiences()
                } catch (error) {
                    this.$toast.error(error.response.data.message)
                    alert(this.$t('common.error'))
                }
            }

            // Reset popup state
            this.showDeletePopup = false
            this.deleteAction = null
            this.itemToDelete = null
        },
        handlePerPageChange(perPage) {
            this.itemsPerPage = Number(perPage)
            setPerPage('experiences_items_per_page', this.itemsPerPage)
            // Like every other filter change (type, search, tag, sort): staying on page N with a
            // larger page size points past the last page, and the pagination offset the drag
            // handler derives from it would then write positions for rows that aren't there.
            this.currentPage = 1
            this.fetchExperiences()
        },
        getEditUrl(item) {
            let url = ''
            const isPublished = item.status && item.status.toLowerCase() === 'publiée'

            if (item.type === 'experience') {
                url = isPublished
                    ? `/back-office/v3/experience/edit/${item.id}/${item.type}`
                    : `/back-office/v3/experience/create/${item.id}/${item.type}`
            } else if (item.type === 'pack') {
                url = isPublished ? `/back-office/v3/package/edit/${item.id}` : `/back-office/v3/package/create/${item.id}`
            } else if (item.type === 'forfait') {
                url = isPublished ? `/back-office/v3/treatment/edit/${item.id}` : `/back-office/v3/treatment/create/${item.id}`
            } else if (item.type === 'abonnement') {
                url = `/back-office/v3/serie/create/${item.id}`
            }

            return url
        },
        openEditTab(item) {
            const url = this.getEditUrl(item)

            if (url) {
                window.location.href = url
            }
        },

        async fetchExperiences() {
            this.isLoading = true
            this.error = null
            try {
                const response = await axios.get('/api/v3/experience/list', {
                    params: {
                        currentPage: this.currentPage,
                        search: this.searchQuery,
                        type: this.selectedType,
                        itemsPerPage: this.itemsPerPage,
                        sort_by: this.sortKey,
                        sort_order: this.sortOrder,
                        tag_id: this.selectedTagId,
                        strategy: this.apiStrategy,
                    },
                    headers: {
                        Authorization: `Bearer ${this.token}`,
                    },
                })

                // Assuming the response structure matches the previous fetch logic:
                // json.data.data -> response.data.data.data
                this.experiences = {
                    data: response.data.data?.data || [],
                    total: response.data.data?.last_page || 0,
                }
            } catch (err) {
                this.error = err.message
                // console.error('API error:', err) // Commented out or removed
            } finally {
                this.isLoading = false
            }
        },
        duplicated(item) {
            this.duplicatedData(item)
        },
        getRowClass(item) {
            if (item._itemKey && item._itemKey === this.duplicatedItemKey) {
                return 'duplicated-row-highlight'
            }
            return ''
        },
        async duplicatedData(item) {
            try {
                const response = await axios.post(
                    `/api/v3/experience/duplicate`,
                    {
                        id: item.id,
                        type: item.type,
                    },
                    {
                        headers: {
                            Authorization: `Bearer ${this.token}`,
                        },
                    }
                )
                const itemKey = `${response.data.id}_${item.type}`
                this.experiences.data.unshift({
                    ...item,
                    id: response.data.id,
                    name: response.data.name,
                    order: response.data.order,
                    state: response.data.state || 'Masquée',
                    _justDuplicated: true,
                    _itemKey: itemKey,
                })
                this.duplicatedItemKey = itemKey
                this.$toast?.success?.(this.$t('experiences.table.duplicationSuccess'))

                // Remove highlight and sort priority after 4 seconds
                setTimeout(() => {
                    this.duplicatedItemKey = null
                    const idx = this.experiences.data.findIndex((e) => e._itemKey === itemKey)
                    if (idx !== -1) {
                        this.$set(this.experiences.data[idx], '_justDuplicated', false)
                    }
                }, 5000)
            } catch (error) {
                this.$toast.error(error.response.data.message)
                console.error('Duplication error:', error)
            }
        },

        async exportData(format) {
            try {
                const response = await axios.get(
                    `/api/v3/experience/export?format=${format}&page=${this.currentPage}&search=${this.searchQuery}&type=${this.selectedType}&itemsPerPage=${this.itemsPerPage}&sort_by=${this.sortKey}&sort_order=${this.sortOrder}&strategy=${this.apiStrategy}`,
                    {
                        responseType: 'blob',
                        headers: {
                            Authorization: `Bearer ${this.token}`,
                        },
                    }
                )

                const blob = new Blob([response.data])
                const url = window.URL.createObjectURL(blob)

                const link = document.createElement('a')
                link.href = url

                const fileName = `export.${format}`
                link.setAttribute('download', fileName)

                document.body.appendChild(link)
                link.click()
                link.remove()
            } catch (error) {
                this.$toast.error(error.response.data.message)
                console.error('Export error:', error)
            }
        },
        async handleDragEnd({ oldIndex, newIndex }) {
            if (oldIndex === newIndex) return

            // Block concurrent drag operations: wait until the previous save completes
            if (this.isSavingPositions) {
                this.$toast?.info?.('Veuillez attendre la fin de la sauvegarde en cours...')
                return
            }

            // Show loading state
            this.isSavingPositions = true

            try {
                // 1. Calculate pagination offset
                // If we're on page 2 with 10 items per page, offset = (2-1) * 10 = 10
                const paginationOffset = (this.currentPage - 1) * this.itemsPerPage

                // 2. Get the items being moved from the filtered view
                const filteredList = [...this.filteredExperiences]

                // 3. Extract the existing global 'order' values BEFORE reordering
                // This is the key to preserving global positions when filtering by a single type.
                const existingOrders = filteredList.map((item) => item.order)

                // 4. Reorder the list (move dragged item to new position)
                const [movedItem] = filteredList.splice(oldIndex, 1)
                filteredList.splice(newIndex, 0, movedItem)

                let positions
                if (this.selectedType === 'tout') {
                    // In "All" mode: use offset-based sequential positions as before
                    // (items from different tables may not have meaningful cross-table order values)
                    positions = filteredList.map((item, index) => ({
                        id: item.id,
                        type: item.type,
                        order: paginationOffset + index + 1,
                    }))
                } else {
                    // In single-type mode: redistribute the order values ALREADY held by the visible
                    // rows, so the global positions of the other types (packs, forfaits…) stay put.
                    // Example: exp1(order=3), exp2(order=4)
                    //   → after drag exp2 to top → exp2.order=3, exp1.order=4
                    //   → pack1(order=1), pack2(order=2) are NOT touched ✅
                    //
                    // `-1` is the "never ordered" sentinel returned by the API
                    // (ExperienceService::mapRowToArray) — same convention as filteredExperiences()
                    // above. It is NOT a position: letting it through emits `order: -1` → HTTP 422.
                    const isAssigned = (o) => Number.isInteger(o) && o >= 0

                    const assigned = existingOrders.filter(isAssigned).sort((a, b) => a - b)
                    const pool = assigned.concat(Array(filteredList.length - assigned.length).fill(null))

                    // Stored order values are heavily duplicated, so the pool is re-spread into a
                    // STRICTLY increasing sequence: tied values would be settled by `updated_at DESC`
                    // backend-side and the move would not stick. Rows without an order consume a
                    // fresh number above the previous slot.
                    let previousSlot = assigned.length ? 0 : paginationOffset
                    const slots = pool.map((value) => {
                        previousSlot = isAssigned(value) ? Math.max(value, previousSlot + 1) : previousSlot + 1
                        return previousSlot
                    })

                    positions = filteredList.map((item, index) => ({
                        id: item.id,
                        type: item.type,
                        order: slots[index],
                    }))
                }

                // 6. Send to API then re-fetch to guarantee frontend-backend sync.
                await this.savePositions(positions)
                await this.fetchExperiences()

                // Force an absolute re-render of the DataTable to wipe SortableJS physical DOM mutations
                // and correctly realign Vue's Virtual DOM with the fresh backend array
                this.tableKey++

                this.$toast?.success?.(this.$t('experiences.table.orderUpdated'))
            } catch (error) {
                // `error.response` is undefined when the request never reached the server; reading
                // `.data.message` blindly threw a TypeError here, which skipped the refetch below
                // and left the table showing SortableJS' DOM mutation as if the move had succeeded.
                this.$toast.error(error?.response?.data?.message || this.$t('experiences.table.orderUpdateFailed'))
                console.error('Position update error:', error)
                await this.fetchExperiences()
                this.tableKey++
            } finally {
                this.isSavingPositions = false
            }
        },

        // Errors bubble up to handleDragEnd, which owns the toast and the refetch:
        // reporting them here too showed the same failure twice.
        async savePositions(positions) {
            const response = await axios.patch(
                '/api/v3/experience/update-positions',
                {
                    positions: positions,
                    type: this.selectedType,
                },
                {
                    headers: {
                        Authorization: `Bearer ${this.token}`,
                    },
                }
            )

            if (!response.data.success) {
                throw new Error(response.data.message || 'Update failed')
            }

            return true
        },
        openCreateTab() {
            // If no specific type is selected, redirect to create-simple to choose type
            if (this.selectedType === 'tout' || !this.selectedType) {
                window.location.href = '/back-office/v3/experience/create-simple'
                return
            }

            // If a specific type is selected, redirect directly to the appropriate create page
            const urls = {
                experience: '/back-office/v3/experience/create',
                pack: '/back-office/v3/package/create',
                forfait: '/back-office/v3/treatment/create',
                abonnement: '/back-office/v3/serie/create',
            }

            if (urls[this.selectedType]) {
                window.location.href = urls[this.selectedType]
            }
        },
        onSort(key) {
            if (this.sortKey === key) {
                if (this.sortOrder === 'asc') {
                    this.sortOrder = 'desc'
                } else {
                    // Third click clears the sort: 'id' is the only sort_by under which the backend
                    // keeps the manual strategy, so this is also how the user gets drag & drop back
                    // (enableDragDrop above) without reloading the page.
                    this.sortKey = 'id'
                    this.sortOrder = 'desc'
                }
            } else {
                this.sortKey = key
                this.sortOrder = 'asc'
            }
            this.currentPage = 1
            this.fetchExperiences()
        },
        async fetchAvailableTags() {
            try {
                const response = await axios.get('/api/v3/experience/tags')
                if (response.data.success) {
                    this.availableTags = response.data.data || []
                }
            } catch (error) {
                this.$toast.error(error.response.data.message)
                console.error('Error fetching tags:', error)
            }
        },
        isTagSelected(item, tagId) {
            if (!item.tag_id || !Array.isArray(item.tag_id)) return false
            return item.tag_id.includes(tagId)
        },
        async updateItemTag(item, tagId) {
            // Set updating state
            this.$set(this.updatingTag, this.getUniqueId(item), true)

            const previousTagId = [...(item.tag_id || [])]
            const previousTagName = item.tag_name

            // Update local state
            if (tagId === null) {
                item.tag_id = []
                item.tag_name = null
                item.tags = []
            } else {
                const selectedTag = this.availableTags.find((t) => t.id === tagId)
                item.tag_id = [tagId]
                item.tag_name = selectedTag ? selectedTag.original_name || selectedTag.name : null
                item.tags = selectedTag ? [{ id: selectedTag.id, name: selectedTag.original_name || selectedTag.name }] : []
            }

            try {
                const response = await axios.patch(
                    '/api/v3/experience/update-tag',
                    {
                        id: item.id,
                        tag_id: tagId,
                        type: item.type,
                    },
                    {
                        headers: {
                            Authorization: `Bearer ${this.token}`,
                        },
                    }
                )

                if (response.data.success) {
                    this.activeTagDropdown = null
                    this.$toast?.success?.('Catégorie mise à jour avec succès')
                } else {
                    // Revert on failure
                    item.tag_id = previousTagId
                    item.tag_name = previousTagName
                    this.$toast?.error?.(response.data.message || 'Erreur lors de la mise à jour des tags')
                }
            } catch (error) {
                this.$toast.error(error.response.data.message)
                // Revert on error
                item.tag_id = previousTagId
                item.tag_name = previousTagName
            } finally {
                this.$set(this.updatingTag, this.getUniqueId(item), false)
            }
        },
        slugify(text) {
            return text
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '')
                .toLowerCase()
                .trim()
                .replace(/\s+/g, '-')
                .replace(/[^\w\-]+/g, '')
                .replace(/\-\-+/g, '-')
        },
        getItemLink(item) {
            if (item.type === 'experience') {
                return `${this.urlFrontReservation}/${item.slug}`
            }
            if (item.type === 'forfait') {
                return `${this.urlFrontReservation.replace('discover/reservation', 'discover/offer')}/${this.slugify(
                    item.name
                )}?id=${item.id}`
            }
            if (item.type === 'pack') {
                return `${this.urlFrontReservation.replace('discover/reservation', 'discover/combos')}/${this.slugify(
                    item.name
                )}?id=${item.id}`
            }
            if (item.type === 'abonnement') {
                return `${this.urlFrontReservation.replace('discover/reservation', 'discover/serie')}/${this.slugify(
                    item.name
                )}?id=${item.id}`
            }
            return '#'
        },
    },
    watch: {
        // Mettre à jour l'état "selectAll" quand la sélection change
        selectedItems: {
            handler(newSelection) {
                this.selectAll = this.paginatedExperiences.length > 0 && newSelection.length === this.paginatedExperiences.length
            },
            deep: true,
        },

        // Réinitialiser la sélection quand on change de page
        currentPage() {
            this.selectedItems = []
            this.selectAll = false
        },

        // Réinitialiser la sélection quand on filtre ou recherche
        filteredExperiences() {
            this.selectedItems = []
            this.selectAll = false
        },
        // Watchers removed because enableDragDrop is now fully reactive via computed property
    },
}
</script>

<style scoped lang="scss">
::v-deep .duplicated-row-highlight {
    background-color: rgba(6, 178, 23, 0.12) !important;
    transition: background-color 0.5s ease;
}

.textGray {
    color: #1a1f37;
}

.btnTrash {
    color: #911414;
}

.bgExperience {
    background-color: rgba(0, 117, 255, var(--tw-bg-opacity));
}

.bgForfait {
    background-color: rgba(219, 168, 0, var(--tw-bg-opacity));
}

.bgAbonnement {
    background-color: rgba(3, 91, 47, var(--tw-bg-opacity));
}

.bgPack {
    background-color: rgba(251, 100, 200, var(--tw-bg-opacity));
}

.dark {
    .textGray {
        color: #bfbfbf;
    }

    .btnTrash {
        color: #ff9c9c;
    }
}

.headerColor {
    background-color: #7d2dbd26;
}
</style>
